// This file configures the initialization of Sentry and PostHog on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';

import { env } from '@/env';

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;

Sentry.init({
  dsn: 'https://a34af5c340a86c67142e1c6a6289bbc1@o24071.ingest.us.sentry.io/4507565769424896',

  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 0.1,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  replaysOnErrorSampleRate: 1.0,

  // You can remove this option if you're not planning to use the Sentry Session Replay feature:
  integrations: [
    Sentry.replayIntegration({
      // Additional Replay configuration goes in here, for example:
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  enabled: process.env.NODE_ENV === 'production',
});

if (env.NEXT_PUBLIC_POSTHOG_KEY && !env.NEXT_PUBLIC_DEV_VM) {
  posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: env.NEXT_PUBLIC_POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_performance: false,
    // Autocapture can send member names and document titles from clicked elements.
    // Keep it disabled until the captured data has been reviewed.
    autocapture: false,
    disable_session_recording: true,
    disable_web_experiments: true,
    // Errors go to Sentry. Unset, this follows the project's remote setting, which is on.
    capture_exceptions: false,
    before_send: [dropNewsLandingPageEvents, skipPersonProfileForPageEvents],
  });
}

function isPageEvent(event: CaptureResult | null) {
  return ['$pageview', '$pageleave'].includes(event?.event ?? '');
}

// /news is the default landing page. $identify also fires there, so only page events are dropped.
function dropNewsLandingPageEvents(event: CaptureResult | null) {
  if (isPageEvent(event) && event?.properties.$pathname === '/news') {
    return null;
  }

  return event;
}

function skipPersonProfileForPageEvents(event: CaptureResult | null) {
  if (!event || !isPageEvent(event)) {
    return event;
  }

  // Overwrite rather than delete: posthog-js drops events whose required properties are undefined.
  return {
    ...event,
    properties: { ...event.properties, $process_person_profile: false },
  };
}
